const WORKER_URL = import.meta.env.VITE_WORKER_URL;

export interface CategoryPhotoRow {
  id: string;
  category_slug: string;
  title: string;
  description: string;
  r2_key: string;
  r2_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

const TOKEN_KEY = 'admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function login(email: string, password: string): Promise<void> {
  const res = await fetch(`${WORKER_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error('Correo o contraseña incorrectos');
  const { token } = (await res.json()) as { token: string };
  localStorage.setItem(TOKEN_KEY, token);
}

export function logout(): void {
  clearToken();
}

function authHeader(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function handleExpired(): never {
  clearToken();
  window.location.replace('/admin');
  throw new Error('Sesión expirada');
}

// ── Photos ────────────────────────────────────────────────────────────────────

export async function getPhotosByCategory(slug: string): Promise<CategoryPhotoRow[]> {
  const res = await fetch(`${WORKER_URL}/api/photos/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error('Error al cargar las fotos');
  return (await res.json()) as CategoryPhotoRow[];
}

export async function getPhotoCounts(): Promise<Record<string, number>> {
  const res = await fetch(`${WORKER_URL}/api/counts`);
  if (!res.ok) return {};
  return (await res.json()) as Record<string, number>;
}

export async function uploadPhoto(
  categorySlug: string,
  file: File,
  title: string,
  description: string,
): Promise<CategoryPhotoRow> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category_slug', categorySlug);
  formData.append('title', title);
  formData.append('description', description);

  const res = await fetch(`${WORKER_URL}/api/photos`, {
    method: 'POST',
    headers: authHeader(),
    body: formData,
  });

  if (res.status === 401) handleExpired();
  if (!res.ok) {
    const msg = await res.text().catch(() => 'Error desconocido');
    throw new Error(msg);
  }
  return (await res.json()) as CategoryPhotoRow;
}

export async function updatePhoto(
  id: string,
  title: string,
  description: string,
): Promise<void> {
  const res = await fetch(`${WORKER_URL}/api/photos/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, description }),
  });
  if (res.status === 401) handleExpired();
  if (!res.ok) throw new Error('Error al actualizar la foto');
}

export async function replacePhoto(id: string, file: File): Promise<CategoryPhotoRow> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${WORKER_URL}/api/photos/${encodeURIComponent(id)}/replace`, {
    method: 'PATCH',
    headers: authHeader(),
    body: formData,
  });
  if (res.status === 401) handleExpired();
  if (!res.ok) {
    const msg = await res.text().catch(() => 'Error desconocido');
    throw new Error(msg);
  }
  return (await res.json()) as CategoryPhotoRow;
}

export async function deletePhoto(id: string): Promise<void> {
  const res = await fetch(`${WORKER_URL}/api/photos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeader(),
  });
  if (res.status === 401) handleExpired();
  if (!res.ok) throw new Error('Error al eliminar la foto');
}

export async function uploadPromoImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${WORKER_URL}/api/promo/upload`, {
    method: 'POST',
    headers: authHeader(),
    body: formData,
  });

  if (res.status === 401) handleExpired();
  if (!res.ok) {
    const msg = await res.text().catch(() => 'Error desconocido');
    throw new Error(msg);
  }
  const { url } = (await res.json()) as { url: string };
  return url;
}

// ── Settings ──────────────────────────────────────────────────────────────────

export async function getSettings(): Promise<Record<string, string>> {
  const res = await fetch(`${WORKER_URL}/api/settings`);
  if (!res.ok) return {};
  return (await res.json()) as Record<string, string>;
}

export async function updateSettings(settings: Record<string, string>): Promise<void> {
  const res = await fetch(`${WORKER_URL}/api/settings`, {
    method: 'PUT',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (res.status === 401) handleExpired();
  if (!res.ok) throw new Error('Error al guardar la configuración');
}
